'use client'
import { createContext, useContext, useEffect, useMemo, useReducer, useState } from 'react'
import Cookies from 'js-cookie'
import { ANALYTICS } from 'constants/analytics'
import { Analytics } from 'components/Analytics'
import { createAnalytics, plugins } from '@achieve/cx-event-horizon/browser'
import { isEqual } from 'utils/shared'

const AnalyticsContext = createContext({
  state: {
    browser_id: undefined,
    auth0_user_id: undefined,
    auth0_email: undefined,
    profile_id: undefined,
    isPageLoaded: false,
    eventQueue: [],
    trackingData: {},
    config: {},
    analyticsInstance: undefined,
    pageVariation: null,
    trackPageComplete: false,
  },
  dispatch: () => {},
})

const analyticsReducer = (state, action) => {
  switch (action.type) {
    case 'SET_AUTH0_USER':
      return {
        ...state,
        auth0_user_id: action.auth0User.user_id,
        auth0_email: action.auth0User.email,
        profile_id: action.auth0User.profile_id,
      }
    case 'SET_PAGE_VARIATION':
      return {
        ...state,
        pageVariation: action.payload.pageVariation,
      }
    case 'SET_TRACKING_DATA':
      return {
        ...state,
        trackingData: action.payload.trackingData,
      }
    case 'SET_PAGE_STATE_LOADING':
      return {
        ...state,
        isPageLoaded: false,
        trackPageComplete: false,
      }
    case 'SET_PAGE_STATE_LOADED':
      return {
        ...state,
        trackingData: action.payload.trackingData,
        isPageLoaded: true,
      }
    case 'SET_TRACK_PAGE_COMPLETE':
      return {
        ...state,
        trackPageComplete: true,
      }

    case 'SET_QUIZ_PAGE_NAME':
      return {
        ...state,
        quiz_page: action.payload.quiz_page,
        quiz_score: action.payload.quiz_score,
      }
    default:
      throw new Error(`Unhandled action type: ${action.type}`)
  }
}

const useConfig = () => {
  const {
    state: { config },
  } = useContext(AnalyticsContext)

  return { config }
}

const usePageVariation = () => {
  const {
    state: { pageVariation },
  } = useContext(AnalyticsContext)
  return { pageVariation: pageVariation ?? globalThis.__acxAnalyticsState?.pageVariation ?? null }
}

const ANALYTICS_DUMMY_CONTEXT = Object.freeze({
  state: {},
  dispatch: () => {},
})

const ANALYTICS_SINGLETON_KEY = '__acxAnalyticsInstance'
const ANALYTICS_STATE_KEY = '__acxAnalyticsState'

const initAnalyticsInstance = (config = {}) => {
  if (!globalThis[ANALYTICS_SINGLETON_KEY]) {
    globalThis[ANALYTICS_SINGLETON_KEY] = createAnalytics({
      application: config.NEXT_PUBLIC_ANALYTICS_APP_NAME,
      debug: parseInt(config.NEXT_PUBLIC_ANALYTICS_DEBUG) === 1,
      plugins: [
        plugins.analyticsFreedom({
          application: config.NEXT_PUBLIC_ANALYTICS_APP_NAME,
          isDebug: parseInt(config.NEXT_PUBLIC_ANALYTICS_DEBUG) === 1,
        }),
        plugins.analyticsTealium({
          application: config.NEXT_PUBLIC_ANALYTICS_APP_NAME,
        }),
      ],
    })
  }

  return { analyticsInstance: globalThis[ANALYTICS_SINGLETON_KEY] }
}

const AnalyticsProvider = ({
  children,
  config,
  pageVariation,
  pageSectionsVariations,
  loadEventProps = {},
  siteEnv,
  siteInfo,
  deferUntilMounted = true,
}) => {
  const [mounted, setMounted] = useState(!deferUntilMounted)

  useEffect(() => {
    if (deferUntilMounted) {
      setMounted(true)
    }
  }, [deferUntilMounted])

  // useEffect to dispatch an action to update the pageVariation state whenever the pageVariation prop changes.
  useEffect(() => {
    if (mounted && !isEqual(state.pageVariation, pageVariation)) {
      dispatch({
        type: 'SET_PAGE_VARIATION',
        payload: {
          pageVariation,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageVariation, mounted])

  const { analyticsInstance } = initAnalyticsInstance(config)
  // The current state. During the first render, it’s set to init(initialArg) or initialArg (if there’s no init).
  // The 2nd argument in the useReducer is just the initial state meaning if we want to update anything in there when data changes
  // we have to manually update the reducer state with a useEffect and dispatch.
  // https://react.dev/reference/react/useReducer

  const [state, dispatch] = useReducer(analyticsReducer, {
    browser_id: Cookies.get(ANALYTICS.BROWSER_ID_KEY_NAME),
    auth0_user_id: undefined,
    auth0_email: undefined,
    profile_id: undefined,
    isPageLoaded: false,
    eventQueue: [],
    trackingData: {},
    config,
    analyticsInstance,
    pageVariation,
    trackPageComplete: false,
    ...(loadEventProps?.quiz_name && {
      quiz_name: loadEventProps?.quiz_name,
      quiz_page: loadEventProps?.quiz_page,
      quiz_score: null,
    }),
  })

  useEffect(() => {
    globalThis[ANALYTICS_STATE_KEY] = {
      ...state,
      config,
      analyticsInstance,
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('acx-analytics-state-change', {
          detail: { isPageLoaded: state.isPageLoaded },
        })
      )
    }
  }, [state, config, analyticsInstance])

  const providerValue = useMemo(
    () => (!mounted ? ANALYTICS_DUMMY_CONTEXT : { state, dispatch }),
    [mounted, state, dispatch]
  )

  return (
    <AnalyticsContext.Provider value={providerValue}>
      {!mounted ? (
        children // early render without <Analytics>
      ) : (
        <Analytics
          pageVariation={pageVariation}
          pageSectionsVariations={pageSectionsVariations}
          loadEventProps={loadEventProps}
          siteEnv={siteEnv}
          siteInfo={siteInfo}
        >
          {children}
        </Analytics>
      )}
    </AnalyticsContext.Provider>
  )
}

export { AnalyticsProvider, AnalyticsContext, useConfig, usePageVariation }
